// Klub / Členství: celá historie toho, co se s členstvím dělo (8.10.2026, zadání Ládi).
//
// Nová členka po zaplacení, zrušení, vrácení zrušení, měsíc zdarma, přechod
// na 3 měsíce. Ve FAPI se všechno provádí samo; tady je jen záznam, co se
// stalo a jestli to FAPI přijalo. Upozornění do zvonečku chodí jen tehdy,
// když se něco ve FAPI nepovede (to je potřeba vyřešit ručně).
//
// Data má klub ve své databázi, admin se ptá přes /api/klub-clenstvi-log.
// Na počítači tabulka, na telefonu řádky (stejně jako stránka Členové).
import React, { useMemo, useState } from "react";
import { UserRoundCog, RefreshCw, UserPlus, UserMinus, Undo2, Gift, CalendarRange, Check, AlertTriangle, Clock, Hand, Search, CreditCard } from "lucide-react";
import { useApiData } from "../../lib/api.js";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, RozbalovaciVyber, ErrorNote, SkeletonRows, EmptyState, Stitek } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice } from "../../components/PrehledDlazdice.jsx";

const AKCE = {
  nova:    { ikona: UserPlus,      pole: "bg-emerald-50 text-emerald-600", nazev: "Nová členka" },
  zrusit:  { ikona: UserMinus,     pole: "bg-rose-50 text-rose-600",       nazev: "Zrušení" },
  obnovit: { ikona: Undo2,         pole: "bg-sky-50 text-sky-600",         nazev: "Vrácení zrušení" },
  darek:   { ikona: Gift,          pole: "bg-amber-50 text-amber-600",     nazev: "Měsíc zdarma" },
  na3:     { ikona: CalendarRange, pole: "bg-brand-100 text-brand-deep",   nazev: "Přechod na 3 měsíce" },
  neplaceni: { ikona: CreditCard,  pole: "bg-orange-50 text-orange-600",   nazev: "Nezaplaceno, klub zavřen" },
};

const FAPI = {
  ok:    { ton: "ok",      ikona: Check },
  ceka:  { ton: "pozor",   ikona: Clock },
  chyba: { ton: "problem", ikona: AlertTriangle },
  rucne: { ton: "pozor",   ikona: Hand },
};

const OBDOBI = { vse: null, d30: 30, d90: 90, rok: 365 };
const NA_STRANKU = 100;

const datum = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
};
const cas = (iso) => {
  const d = new Date(iso);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const sklon = (n, a, b, c) => (n === 1 ? a : n > 1 && n < 5 ? b : c);

function IkonaAkce({ akce }) {
  const a = AKCE[akce] || AKCE.nova;
  return (
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${a.pole}`} title={a.nazev}>
      <a.ikona className="h-[18px] w-[18px]" />
    </span>
  );
}

function StavFapi({ fapi }) {
  const f = FAPI[fapi?.stav] || FAPI.rucne;
  return <Stitek ton={f.ton} ikona={f.ikona} className="!whitespace-normal">{fapi?.text || "Udělat ve FAPI ručně"}</Stitek>;
}

function Popis({ z }) {
  return (
    <>
      <div className="font-semibold text-slate-800">{(AKCE[z.akce] || AKCE.nova).nazev}</div>
      {z.detail && <div className="mt-0.5 leading-relaxed text-slate-600">{z.detail}</div>}
      {z.duvod && <div className="mt-0.5 text-slate-500">Důvod: {z.duvod}</div>}
    </>
  );
}

export default function KlubClenstviLog() {
  const { data, loading, error, refetch } = useApiData("/api/klub-clenstvi-log", { bezKese: true });
  const uzka = useUzkaObrazovka();
  const [typ, setTyp] = useState("vse");
  const [fapi, setFapi] = useState("vse");
  const [obdobi, setObdobi] = useState("vse");
  const [hledani, setHledani] = useState("");
  const [limit, setLimit] = useState(NA_STRANKU);

  const zaznamy = data?.zaznamy || [];
  const vObdobi = useMemo(() => {
    const dni = OBDOBI[obdobi];
    if (!dni) return zaznamy;
    const od = Date.now() - dni * 864e5;
    return zaznamy.filter((z) => new Date(z.cas).getTime() >= od);
  }, [zaznamy, obdobi]);

  const pocet = (k) => vObdobi.filter((z) => z.akce === k).length;
  const pocetFapi = (k) => vObdobi.filter((z) => (z.fapi?.stav || "rucne") === k).length;
  const kVyreseni = pocetFapi("chyba") + pocetFapi("rucne");

  const q = hledani.trim().toLowerCase();
  const videt = vObdobi
    .filter((z) => typ === "vse" || z.akce === typ)
    .filter((z) => fapi === "vse" || (z.fapi?.stav || "rucne") === fapi)
    .filter((z) => !q || `${z.jmeno} ${z.email}`.toLowerCase().includes(q));

  return (
    <Stranka>
      <GradientHeader
        title="Členství v klubu"
        icon={UserRoundCog}
        loading={loading}
        napoveda="Celá historie: kdo zaplatil, zrušil, vrátil zrušení, vzal si měsíc zdarma nebo přešel na 3 měsíce, a co se kvůli tomu samo změnilo ve FAPI. Nic z toho se nedělá ručně. Červený nebo žlutý štítek znamená, že FAPI změnu nepřijalo a je potřeba ji ve FAPI udělat ručně, o tom vám přijde i upozornění."
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading }]}
      />

      {error && <ErrorNote onRetry={refetch}>Záznamy se nepodařilo načíst: {String(error)}</ErrorNote>}

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={UserRoundCog}
          nazev="Historie členství"
          podtitulek={`${pocet("nova")} ${sklon(pocet("nova"), "nová členka", "nové členky", "nových členek")}, ${pocet("zrusit")} ${sklon(pocet("zrusit"), "zrušení", "zrušení", "zrušení")}, ${pocet("na3")} ${sklon(pocet("na3"), "přechod", "přechody", "přechodů")} na 3 měsíce, ${pocet("darek")} ${sklon(pocet("darek"), "měsíc", "měsíce", "měsíců")} zdarma`}
          odznak={kVyreseni ? `${kVyreseni} k vyřešení` : "Vše provedeno"}
          odznakTon={kVyreseni ? "cerveny" : "bily"}
        />

        <div className="space-y-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:p-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <RozbalovaciVyber
              stitek="Co se stalo"
              aria-label="Filtr podle typu"
              value={typ}
              onChange={(v) => { setTyp(v); setLimit(NA_STRANKU); }}
              items={[
                { key: "vse", label: `Vše (${vObdobi.length})` },
                ...Object.entries(AKCE).map(([k, a]) => ({ key: k, label: `${a.nazev} (${pocet(k)})` })),
              ]}
            />
            <RozbalovaciVyber
              stitek="FAPI"
              aria-label="Filtr podle výsledku ve FAPI"
              value={fapi}
              onChange={(v) => { setFapi(v); setLimit(NA_STRANKU); }}
              items={[
                { key: "vse", label: "Vše" },
                { key: "ok", label: `Provedeno (${pocetFapi("ok")})` },
                { key: "chyba", label: `Nepovedlo se (${pocetFapi("chyba")})` },
                { key: "rucne", label: `Ručně (${pocetFapi("rucne")})` },
              ]}
            />
            <RozbalovaciVyber
              stitek="Období"
              aria-label="Období"
              value={obdobi}
              onChange={(v) => { setObdobi(v); setLimit(NA_STRANKU); }}
              items={[
                { key: "vse", label: "Celá historie" },
                { key: "d30", label: "Posledních 30 dní" },
                { key: "d90", label: "Posledních 90 dní" },
                { key: "rok", label: "Poslední rok" },
              ]}
            />
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="field pl-9" placeholder="Hledat podle jména nebo e-mailu"
              value={hledani} onChange={(e) => { setHledani(e.target.value); setLimit(NA_STRANKU); }} />
          </div>
        </div>

        {loading && !data && <div className="p-3 sm:p-4"><SkeletonRows rows={6} /></div>}

        {data && (videt.length === 0 ? (
          <EmptyState icon={UserRoundCog} title="Žádný záznam" hint="Zkus jiný filtr nebo vymaž hledání." />
        ) : uzka ? (
          <div className="divide-y divide-slate-100">
            {videt.slice(0, limit).map((z) => (
              <div key={z.id} className="flex items-start gap-3 px-4 py-4">
                <IkonaAkce akce={z.akce} />
                <div className="min-w-0 flex-1 text-sm">
                  <div className="text-xs text-slate-400">{datum(z.cas)} {cas(z.cas)}</div>
                  <div className="font-semibold text-slate-800">{z.jmeno || z.email}</div>
                  <div className="mt-1"><Popis z={z} /></div>
                  <div className="mt-2"><StavFapi fapi={z.fapi} /></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 text-left font-semibold sm:px-5">Kdy</th>
                  <th className="px-2 py-3 text-left font-semibold">Členka</th>
                  <th className="px-2 py-3 text-left font-semibold">Co se stalo</th>
                  <th className="px-4 py-3 text-left font-semibold sm:px-5">Ve FAPI</th>
                </tr>
              </thead>
              <tbody>
                {videt.slice(0, limit).map((z) => (
                  <tr key={z.id} className="border-b border-slate-100 align-top hover:bg-slate-50/70">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600 tabular-nums sm:px-5">
                      {datum(z.cas)}<div className="text-xs text-slate-400">{cas(z.cas)}</div>
                    </td>
                    <td className="min-w-[200px] px-2 py-3">
                      <div className="font-semibold text-slate-800">{z.jmeno || "Bez jména"}</div>
                      <a href={`mailto:${z.email}`} className="break-words text-slate-500 hover:text-plum-600">{z.email}</a>
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex items-start gap-3">
                        <IkonaAkce akce={z.akce} />
                        <div className="min-w-0"><Popis z={z} /></div>
                      </div>
                    </td>
                    <td className="w-[260px] px-4 py-3 sm:px-5"><StavFapi fapi={z.fapi} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {data && videt.length > limit && (
          <div className="border-t border-slate-100 p-3 text-center sm:p-4">
            <button type="button" className="btn-secondary" onClick={() => setLimit((l) => l + NA_STRANKU)}>
              Zobrazit dalších {Math.min(NA_STRANKU, videt.length - limit)} z {videt.length - limit}
            </button>
          </div>
        )}
      </KartaPrehledu>
    </Stranka>
  );
}
